import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCity } from '../contexts/CityContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Loading } from '../components/Loading';

// LE-020 (S4-c): criação e edição de eventos.
// POST/PATCH /api/events — autenticado, CSRF automático (api.js).
// Modo edição: /eventos/:id/editar (povoa do GET /api/events/:id).

export function CreateEventPage() {
  const navigate = useNavigate();
  const { id } = useParams();        // edit mode: event id
  const [searchParams] = useSearchParams();
  const { selectedCity } = useCity();
  const { user } = useAuth();

  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [businesses, setBusinesses] = useState([]);

  const [form, setForm] = useState({
    name: '',
    description: '',
    date: '',
    time_start: '',
    time_end: '',
    address: '',
    image_url: '',
    business_id: '',
  });

  // Carrega dados do evento em modo edição + lista de comércios do comerciante
  useEffect(() => {
    if (!isEdit) return;
    const fetch = async () => {
      try {
        const { data } = await api.get(`/events/${id}`);
        setForm({
          name: data.name || '',
          description: data.description || '',
          date: data.date ? data.date.toString().slice(0, 10) : '',
          time_start: data.time_start || '',
          time_end: data.time_end || '',
          address: data.address || '',
          image_url: data.image_url || '',
          business_id: data.business_id ? String(data.business_id) : '',
        });
      } catch (err) {
        setError(err.response?.data?.error?.message || 'Evento não encontrado');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id, isEdit]);

  // Lista comércios do comerciante para dropdown
  useEffect(() => {
    api.get('/merchant/businesses').then(({ data }) => {
      setBusinesses(data.data || []);
    }).catch(() => {});
  }, []);

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        city_id: selectedCity?.id,
        name: form.name,
        description: form.description || undefined,
        date: form.date,
        time_start: form.time_start || undefined,
        time_end: form.time_end || undefined,
        address: form.address || undefined,
        image_url: form.image_url || undefined,
        business_id: form.business_id ? parseInt(form.business_id, 10) : undefined,
      };

      if (isEdit) {
        await api.patch(`/events/${id}`, payload);
      } else {
        await api.post('/events', payload);
      }

      navigate(isEdit ? `/evento/${id}` : '/eventos');
    } catch (err) {
      const code = err.response?.data?.error?.code;
      const msg = err.response?.data?.error?.message;
      if (code === 'INVALID_CITY' || code === 'BUSINESS_CITY_MISMATCH') {
        setError('A cidade ou comércio informado é inválido.');
      } else if (code === 'BUSINESS_NOT_FOUND') {
        setError('Comércio não encontrado ou inativo.');
      } else if (code === 'FORBIDDEN') {
        setError('Você não pode editar este evento.');
      } else {
        setError(msg || 'Erro ao salvar evento.');
      }
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '11px 12px', borderRadius: 10, border: '1px solid var(--nc-border)',
    fontSize: 14, outline: 'none', color: 'var(--nc-text)', boxSizing: 'border-box',
  };

  const labelStyle = {
    fontSize: 12, fontWeight: 600, color: 'var(--nc-text-secondary)', marginBottom: 4, display: 'block',
  };

  if (loading) return <Loading message="Carregando..." />;

  return (
    <div className="nc-page">
      <div style={{
        background: 'linear-gradient(135deg, var(--nc-boost) 0%, #5b21b6 100%)',
        color: 'var(--nc-on-primary)', padding: '18px 16px 22px', borderRadius: '0 0 22px 22px',
      }}>
        <div className="nc-row">
          <button onClick={() => navigate(-1)} aria-label="Voltar" className="nc-iconbtn">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="nc-page-title">
            {isEdit ? 'Editar evento' : 'Criar evento'}
          </h1>
        </div>
        <p style={{ margin: '4px 0 0 28px', fontSize: 12, opacity: 0.85 }}>
          {selectedCity?.name} / {selectedCity?.state}
        </p>
      </div>

      <div style={{ padding: 16, maxWidth: 500, margin: '0 auto' }}>
        {error && (
          <div role="alert" style={{
            padding: 12, borderRadius: 10, background: 'var(--nc-danger-soft)', border: '1px solid var(--nc-danger-border)',
            color: 'var(--nc-danger-text)', fontSize: 13, marginBottom: 14,
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Nome do evento *</label>
            <input
              type="text"
              value={form.name}
              onChange={set('name')}
              required
              maxLength={200}
              placeholder="Ex: Festa Junina, Feira de Artesanato..."
              aria-label="Nome do evento"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Descrição</label>
            <textarea
              value={form.description}
              onChange={set('description')}
              maxLength={2000}
              rows={4}
              placeholder="Descreva o evento..."
              aria-label="Descrição"
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Data *</label>
              <input
                type="date"
                value={form.date}
                onChange={set('date')}
                required
                aria-label="Data"
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Horário início</label>
              <input
                type="time"
                value={form.time_start}
                onChange={set('time_start')}
                aria-label="Horário início"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Horário fim</label>
              <input
                type="time"
                value={form.time_end}
                onChange={set('time_end')}
                aria-label="Horário fim"
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Endereço</label>
            <input
              type="text"
              value={form.address}
              onChange={set('address')}
              maxLength={300}
              placeholder="Ex: Praça Central, Rua das Flores, 100"
              aria-label="Endereço"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>URL da imagem</label>
            <input
              type="url"
              value={form.image_url}
              onChange={set('image_url')}
              maxLength={500}
              placeholder="https://..."
              aria-label="URL da imagem"
              style={inputStyle}
            />
          </div>

          {/* Business selector (merchants) */}
          {businesses.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Comércio associado (opcional)</label>
              <select
                value={form.business_id}
                onChange={set('business_id')}
                aria-label="Comércio associado"
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="">Nenhum</option>
                {businesses
                  .filter((b) => b.is_approved && b.is_active)
                  .map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
              </select>
            </div>
          )}

          <button
            type="submit"
            disabled={saving}
            style={{
              width: '100%', padding: '14px 0', borderRadius: 12, border: 'none', cursor: 'pointer',
              background: saving ? 'rgba(124,58,237,0.7)' : 'linear-gradient(135deg,#7c3aed,#5b21b6)',
              color: 'var(--nc-on-primary)', fontSize: 15, fontWeight: 700,
              transition: 'all 0.15s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            {saving && <span className="nc-pulse" style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: 'var(--nc-surface)' }} />}
            {saving ? 'Salvando...' : isEdit ? 'Salvar alterações' : 'Criar evento 🎉'}
          </button>
        </form>
      </div>
    </div>
  );
}
